import { useEffect, useCallback } from 'react';
import { useShallowMemo } from 'app/utils/react';
import { CancelToken } from 'app/utils/cancelToken';
import { LoadingState } from 'app/containers/state/LoadingState';
import {
  useAutoLoader,
} from 'app/containers/state/AutoLoader';
import {
  createAutoLoaderCache,
  AutoLoaderCache,
  CacheProvider,
  useCache,
  addReference,
  loadIntoCache,
  clearResult,
  clearPromiseForReload,
  getCachedState,
  clearCache,
  waitForPending,
  hasPending,
} from './AutoLoaderCache';
import {
  ServerRenderLoader,
  ServerRenderLoaderProvider,
  useServerRenderLoader,
} from './ServerRenderLoader';
import throwOnError from './throwOnError';

export {
  CacheProvider,
  createAutoLoaderCache,
  AutoLoaderCache,
  clearCache,
  waitForPending,
  hasPending,
};

export {
  ServerRenderLoader,
  ServerRenderLoaderProvider,
};

export { throwOnError };

export type UseLoaderReturnType<ValueType> = LoadingState<ValueType> & {
  reload(): void;
  clear(): void;
};

export type UseLoaderType<ArgsType, ValueType> = (
  { args }: { args: ArgsType },
) => UseLoaderReturnType<ValueType>;

/**
 * @param {Function} load - Async function that takes `args` object and `cancelToken`
 * @param {number} options.cacheTimeout - (Optional) Milliseconds that entry
 * should remain in cache after there are no more components using it. Defaults
 * to 0.
 *
 * # Usage
 *
 * Takes the same kind of `load` function that you would pass to
 * `useAutoLoader`, and returns a `useLoader` hook that behaves exactly like
 * `useAutoLoader`, but that does not require passing a `load` function (it will
 * use the `load` function passed to `makeCachedLoader`).
 *
 * Note: `makeCachedLoader` must not be called inside the component, or else the
 * caching behavior will not work properly. Internally, it creates a unique
 * symbol that should be shared across components and across renders.
 *
 * Example:
 *
 * ```
 * const { useLoader } = makeCachedLoader(
 *   async (args: { query: string }, cancelToken) => (
 *     return await doSomeSearchQuery(query, cancelToken);
 *   },
 * );
 *
 * const MyComponent: React.FC<{ query: string }> = ({ query }) => {
 *   const { stage, value, error } = useLoader({ args: { query } });
 *
 *   // Render success/loading/error states
 * };
 * ```
 *
 * # Goals
 *
 * makeCachedLoader and AutoLoaderCache are designed to allow caching of
 * `LoadingState`s both across components and across renders of the entire app.
 * This is useful for several different purposes:
 *
 * 1. For doing server-side rendering with ReactDOMServer
 *
 * ReactDOMServer does not support loading of data while rendering, so you must
 * load all data upfront before rendering. We can work around this by using
 * using makeCachedLoader+AutoLoaderCache to save the loads across app renders,
 * and try rendering the app several times until we are finished loading all of
 * the critical app data needed to server render.
 *
 * Note that this abstraction currently does not support client side hydration.
 * Supporting hydration would likely require 1) allowing the user to specify a
 * unique `cacheKey` for each call to `makeCachedLoader` (and producing a
 * warning if there is a conflict between any of these keys) and 2) adding
 * methods to `AutoLoaderCache` that allow its contents to be
 * serialized/deserialized (and producing a warning if the user passes any
 * `args` that cannot be serialized).
 *
 * 2. To avoid manual hoisting of loading logic
 *
 * One of the main advantages of React is that we can encapsulate logic in a
 * single component and then re-use that in several places. However, when it
 * comes to loading data this can be problematic because it may lead to
 * unnecessarily loading the same data multiple times (e.g. if a component that
 * loads data is re-used in several places on the same page).
 *
 * One way of working around this is to "hoist" the loading logic out of the
 * component that uses the data to a higher place in the React tree, and pass it
 * down to the component via props or context. This works, but requires some
 * manual work/boilerplate, and removes some encapsulation: now the logic for
 * fetching the data and using it have to be in separate places.
 *
 * AutoLoaderCache can be used to make sure that only the first load is actually
 * made, and that the result is shared with all instances of the component,
 * while still keeping the logic in one place.
 *
 * To prevent "waterfalling" of loads, you can still manually start pre-loading
 * the data at a higher place in the tree and depend on the cache to avoid
 * duplicate loads. However, this is not required and is purely for performance
 * improvement.
 *
 * Note that, to prevent overzealous caching, by default "cached" loads expire
 * immediately after no component is using that data anymore, so sharing of
 * loads will only happen if the components will be in the tree at the same
 * time. This can be configured with the `cacheTimeout` option (by providing a
 * number in milliseconds), if you want to make some data more long-lived.
 *
 * 3. Code splitting
 *
 * When lazily loading components, after asynchronously loading the module we
 * need to save the module somewhere. The above-mentioned caching functionality
 * can be easily re-used for this purpose as well, by using a `cacheTimeout` of
 * `Infinity` so the module is never removed from the cache.
 *
 * This is really just a special case of the the hoisting/caching problem
 * mentioned above.
 *
 * # Prior Art
 *
 * - This talk is specific to React Suspense, but the beginning of the talk
 *   describes some of the same problems that this is trying to solve:
 *   https://www.youtube.com/watch?v=z-6JC0_cOns
 * - This takes some inspiration from react-cache (now abandoned):
 *   https://github.com/facebook/react/tree/master/packages/react-cache
 */
export function makeCachedLoader<ArgsType, ResultType>(
  load: (args: ArgsType, cancelToken: CancelToken) => Promise<ResultType>,
  options: {
    /**
     * Milliseconds to keep item in cache after it is no longer being used.
     */
    cacheTimeout?: number,
    /**
     * If true, this should not be loaded when server-rendering.
     */
    nonCritical?: boolean,
  } = {},
) {
  const {
    cacheTimeout = 0, // Default to expiring immediately
    nonCritical = false,
  } = options;

  const cacheKey = Symbol('cache key');

  const useLoader: UseLoaderType<
    ArgsType,
    ResultType
  > = ({ args }) => {
    const cache = useCache();

    // Shallow-memoize args just to prevent unnecessarily calling
    // `addReference`/`releaseReference` after every render.
    const memoizedArgs = useShallowMemo(args);

    useEffect(() => {
      if (cache) {
        const releaseReference = addReference(cache, cacheKey, memoizedArgs, cacheTimeout);

        return () => releaseReference();
      }
    }, [cache, memoizedArgs]);

    const serverRenderLoader = useServerRenderLoader();
    if (serverRenderLoader && !nonCritical) {
      serverRenderLoader.addLoad({
        cacheKey,
        args,
        load,
      });
    }

    const autoLoaderResult = useAutoLoader({
      args,
      load: (async (loadArgs: ArgsType, cancelToken: CancelToken) => {
        if (!cache) {
          // When no cache is provided, just behave like useAutoLoader.
          return await load(loadArgs, cancelToken);
        } else {
          // When using the cache, we don't use the cancelToken given to use by
          // useAutoLoader. Instead, we rely on the reference counting done by
          // `addReference` to determine when the request should be cancelled.
          return await loadIntoCache(cache, {
            cacheKey,
            cacheTimeout,
            args,
            load,
          });
        }
        // We need the `as` because TypeScript is not able to tell that the
        // types are equivalent due to the generic.
      }),
    });

    const clear = useCallback(() => {
      // We don't need to call autoLoaderResult.clear(), because we do not use
      // the state from useAutoLoader() at all when we're using the cache.
      if (cache) {
        clearResult(cache, cacheKey, memoizedArgs);
      }
    }, [cache, memoizedArgs]);

    const baseReload = autoLoaderResult.reload;

    const reload = useCallback(() => {
      if (cache) {
        clearPromiseForReload(cache, cacheKey, memoizedArgs);
      }

      // Don't allow overriding args when re-loading, because the cache
      // reference-counting relies on the value of `args`.
      //
      // We still use the reload from useAutoLoader because we want loading to
      // always be queued.
      baseReload();
    }, [cache, memoizedArgs, baseReload]);

    if (!cache) {
      // When no cache is provided, just behave like useAutoLoader.
      return autoLoaderResult;
    } else {
      const cachedState = getCachedState(cache, { cacheKey, args });

      return {
        reload,
        clear,
        ...cachedState as LoadingState<ResultType>,
      };
    }
  };

  // Return an object with a named function to encourage the use of a name that
  // starts with `use*`, since this is a hook and must be named appropriately to
  // work with the hooks linting rule.
  return { useLoader };
}
